import { useState, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { RotateCcw, Copy, Trash2, Loader2, Search, X, ChartNoAxesColumn } from "lucide-react";
import { useReturns, useDeleteReturn, useStaleReturnsCount } from "@/hooks/use-returns";
import { useStore } from "@/hooks/use-stores";
import { useActiveStore } from "@/hooks/use-active-store";
import { useSession } from "@/hooks/use-auth";
import { hasRole } from "@/lib/roles";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { TablePagination } from "@/components/ui/table-pagination";
import {
  STATUS_LABEL,
  STATUS_BADGE,
  REQUEST_TYPE_LABEL,
  formatDaysAgo,
  isUnresolvedStatus,
  STALE_DAYS,
} from "@/lib/return-labels";
import { returnsRoute } from "@/lib/router";

/** Star zahtjev: neriješen status + createdAt star STALE_DAYS+ dana. Vraća broj dana ili null ako nije star. */
function staleAgeDays(status: string, createdAt: number): number | null {
  if (!isUnresolvedStatus(status)) return null;
  const ageDays = Math.floor((Date.now() / 1000 - createdAt) / 86400);
  return ageDays >= STALE_DAYS ? ageDays : null;
}

const STATUS_OPTIONS = [
  { value: "all", label: "Svi" },
  { value: "new", label: "Novo" },
  { value: "in_progress", label: "U obradi" },
  { value: "approved", label: "Odobreno" },
  { value: "rejected", label: "Odbijeno" },
  { value: "refund_completed", label: "Povrat izvršen" },
  { value: "exchange_completed", label: "Zamjena izvršena" },
];

const REQUEST_TYPE_OPTIONS = [
  { value: "all", label: "Svi tipovi" },
  { value: "mijenja", label: "Mijenja" },
  { value: "vraca", label: "Vraća" },
  { value: "mijenja_dodatno", label: "Mijenja + dodatno" },
];

export function ReturnsPage() {
  const search = returnsRoute.useSearch();
  const navigate = returnsRoute.useNavigate();
  const [searchInput, setSearchInput] = useState(search.q ?? "");

  const statusFilter = search.status ?? "all";
  const typeFilter = search.type ?? "all";
  const page = search.page ?? 1;

  function setSearchParams(patch: Partial<typeof search>, resetPage = true) {
    navigate({
      search: (prev) => ({ ...prev, ...patch, ...(resetPage ? { page: 1 } : {}) }),
    });
  }
  const setStatusFilter = (v: string) => setSearchParams({ status: v });
  const setTypeFilter = (v: string) => setSearchParams({ type: v });
  const setPage = (p: number) => setSearchParams({ page: p }, false);
  const staleFilter = search.stale === true;
  const toggleStale = () => setSearchParams({ stale: !staleFilter });

  // Debounce tekst pretrage u URL (300ms)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (searchInput === (search.q ?? "")) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchParams({ q: searchInput }), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(search.q ?? "");
  }, [search.q]);

  const { storeId } = useActiveStore();
  const { data: store } = useStore(storeId);

  const { data: staleData } = useStaleReturnsCount(storeId);
  const staleCount = staleData?.count ?? 0;

  const filters: { storeId?: string; status?: string; requestType?: string; search?: string; dateFrom?: string; dateTo?: string; page?: number; stale?: boolean } = { page };
  if (storeId) filters.storeId = storeId;
  if (statusFilter !== "all") filters.status = statusFilter;
  if (typeFilter !== "all") filters.requestType = typeFilter;
  const q = (search.q ?? "").trim();
  if (q) filters.search = q;
  if (search.from) filters.dateFrom = search.from;
  if (search.to) filters.dateTo = search.to;
  if (staleFilter) filters.stale = true;

  const { data, isLoading } = useReturns(filters);
  const returns = data?.items;
  const totalPages = data ? Math.ceil(data.total / 25) : 0;
  const { user } = useSession();
  const canDelete = hasRole(user, "admin");
  const deleteReturn = useDeleteReturn();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function handleDelete(id: string, ref: string, e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    if (!confirm(`Obrisati zahtjev za povrat ${ref}?`)) return;
    setDeletingId(id);
    try {
      await deleteReturn.mutateAsync(id);
      toast.success("Zahtjev obrisan");
    } catch (err: any) {
      toast.error(err.message || "Greška pri brisanju");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <RotateCcw className="h-4 w-4" />
        <h2 className="text-sm font-medium">Zahtjevi za povrat</h2>
        {data && <span className="text-xs text-muted-foreground">({data.total})</span>}
        <Link
          to="/returns/analitika"
          search={{ days: 30 }}
          className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 text-xs ring-1 ring-foreground/10 hover:bg-foreground/5 transition-colors"
        >
          <ChartNoAxesColumn className="h-3.5 w-3.5" />
          Analitika
        </Link>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative w-full sm:w-auto sm:flex-1 sm:min-w-[260px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Pretraži ref, ime, telefon, email, narudžbu…"
            className="h-8 pl-7 text-xs"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REQUEST_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DatePicker
          value={search.from ?? ""}
          onChange={(v) => setSearchParams({ from: v })}
          placeholder="Od"
          className="h-8 text-xs"
        />
        <DatePicker
          value={search.to ?? ""}
          onChange={(v) => setSearchParams({ to: v })}
          placeholder="Do"
          className="h-8 text-xs"
        />

        <Button
          variant={staleFilter ? "destructive" : "outline"}
          className="h-8 text-xs"
          // Kad je filter uključen mora ostati klikabilan i pri nuli — inače se aktivan
          // filter (npr. iz bookmarkovanog ?stale=1) ne može isključiti sa samog dugmeta.
          disabled={!staleFilter && !staleCount}
          onClick={toggleStale}
        >
          Starije od {STALE_DAYS} dana ({staleCount})
        </Button>

        {(statusFilter !== "all" || typeFilter !== "all" || (search.q ?? "") || search.from || search.to || staleFilter) && (
          <Button
            variant="ghost"
            className="h-8 text-xs"
            onClick={() => {
              setSearchInput("");
              navigate({ search: () => ({ status: "all", type: "all", q: "", from: "", to: "", page: 1, stale: false }) });
            }}
          >
            <X className="h-3 w-3 mr-1.5" />
            Poništi
          </Button>
        )}

        {store?.slug && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/return/${store.slug}`);
                    toast.success("Link kopiran");
                  }}
                >
                  <Copy className="h-3 w-3 mr-1.5" />
                  Kopiraj link
                </Button>
              </TooltipTrigger>
              <TooltipContent>{window.location.origin}/return/{store.slug}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : (
        <div className="ring-1 ring-foreground/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-2 text-left font-medium">Ref</th>
                <th className="p-2 text-left font-medium">Kupac</th>
                <th className="p-2 text-left font-medium hidden sm:table-cell">Telefon</th>
                <th className="p-2 text-left font-medium hidden md:table-cell">Tip</th>
                <th className="p-2 text-left font-medium">Status</th>
                <th className="p-2 text-left font-medium hidden sm:table-cell">Datum</th>
                {canDelete && <th className="p-2 w-8" aria-label="Akcije" />}
              </tr>
            </thead>
            <tbody>
              {returns?.map((r: any) => (
                  <tr key={r.id} className="border-b hover:bg-muted/50 transition-colors">
                    <td className="p-2">
                      <Link
                        to="/returns/$returnId"
                        params={{ returnId: r.id }}
                        className="font-mono text-blue-400 hover:underline"
                      >
                        {r.referenceNumber}
                      </Link>
                    </td>
                    <td className="p-2 font-medium">{r.firstName} {r.lastName}</td>
                    <td className="p-2 text-muted-foreground hidden sm:table-cell">{r.phone || "—"}</td>
                    <td className="p-2 text-muted-foreground hidden md:table-cell">{REQUEST_TYPE_LABEL[r.requestType] || r.requestType}</td>
                    <td className="p-2">
                      <Badge variant="outline" className={STATUS_BADGE[r.status]}>
                        {STATUS_LABEL[r.status] || r.status}
                      </Badge>
                    </td>
                    <td className="p-2 text-muted-foreground hidden sm:table-cell">
                      <div className="flex items-center gap-1.5">
                        <span>{formatDate(new Date(r.createdAt * 1000).toISOString())}</span>
                        {(() => {
                          const ageDays = staleAgeDays(r.status, r.createdAt);
                          if (ageDays === null) return null;
                          return (
                            <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-500/20">
                              {formatDaysAgo(ageDays)}
                            </Badge>
                          );
                        })()}
                      </div>
                    </td>
                    {canDelete && (
                      <td className="p-2 w-8">
                        <button
                          type="button"
                          onClick={(e) => handleDelete(r.id, r.referenceNumber, e)}
                          disabled={deletingId === r.id}
                          className="text-muted-foreground hover:text-red-400 transition-colors disabled:opacity-50"
                          aria-label="Obriši zahtjev"
                          title="Obriši zahtjev"
                        >
                          {deletingId === r.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Trash2 className="h-3.5 w-3.5" />}
                        </button>
                      </td>
                    )}
                  </tr>
              ))}
              {returns?.length === 0 && (
                <tr>
                  <td colSpan={canDelete ? 7 : 6} className="p-6 text-center text-muted-foreground">
                    Nema zahtjeva za povrat
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
